import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { AuthLayout } from "./AuthLayout";

interface VerifyEmailProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
}

export function VerifyEmail({ email, onVerify, onResend, onBack, isLoading, error, onClearError }: VerifyEmailProps) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleChange = useCallback((i: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;
    const next = [...digits];
    next[i] = value;
    setDigits(next);
    if (value && i < 5) inputRefs.current[i + 1]?.focus();
  }, [digits]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = text[i] ?? "";
    setDigits(next);
    const focus = Math.min(text.length, 5);
    inputRefs.current[focus]?.focus();
  }, [digits]);

  const handleKeyDown = useCallback((i: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  }, [digits]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) return;
    onClearError();
    await onVerify(code);
  }

  async function handleResend() {
    if (resendCooldown > 0) return;
    setResendSuccess(false);
    await onResend();
    setResendCooldown(60);
    setResendSuccess(true);
  }

  const code = digits.join("");
  const isComplete = code.length === 6;

  return (
    <AuthLayout>
      <div className="relative w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#22d3ee]/10 border border-[#22d3ee]/20 mb-5 relative">
            <div className="absolute inset-0 rounded-2xl bg-[#22d3ee]/5 blur-md" />
            <svg className="relative w-8 h-8 text-[#22d3ee]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Verify your email</h1>
          <p className="mt-1.5 text-sm text-slate-500">
            Code sent to <span className="text-slate-300 font-mono">{email}</span>
          </p>
        </div>

        <div className="relative p-px rounded-2xl bg-gradient-to-br from-[#22d3ee]/25 via-[#1a2640] to-brand-700/30">
          <div className="bg-[#0d1424] rounded-2xl p-8 shadow-card-lg">

            {error && (
              <div className="mb-5 flex items-start gap-3 bg-red-950/30 border border-red-700/40 rounded-xl p-3.5 animate-fade-in">
                <svg className="w-4 h-4 text-red-400 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-red-300 leading-relaxed">{error}</p>
              </div>
            )}

            {resendSuccess && (
              <div className="mb-5 flex items-center gap-3 bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-3.5 animate-fade-in">
                <svg className="w-4 h-4 text-emerald-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-emerald-300">Code resent - check your inbox.</p>
              </div>
            )}

            <p className="text-xs text-slate-500 mb-3 leading-relaxed">
              Enter the 6-digit verification code we sent to your email. It expires in 10 minutes.
            </p>

            <div className="flex items-start gap-2 mb-6">
              <svg className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-sm text-slate-400 leading-relaxed">
                The email is from{" "}
                <span className="text-slate-300 font-medium">noreply@novasynth.in</span>{" "}
                with the subject{" "}
                <span className="text-slate-300 font-medium">"Your NovaSynth verification code"</span>. Check your spam folder if you don't see it.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* OTP boxes */}
              <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    disabled={isLoading}
                    className={[
                      "w-11 h-14 text-center text-xl font-mono font-bold rounded-xl border",
                      "bg-[#070d1a] text-slate-100",
                      "focus:outline-none focus:ring-1 transition-all duration-200",
                      "disabled:opacity-40 disabled:cursor-not-allowed",
                      d
                        ? "border-brand-500/70 ring-brand-500/30 text-brand-300 shadow-glow-brand-sm"
                        : "border-[#1a2640] hover:border-[#263350] focus:border-brand-600/50 focus:ring-brand-600/30",
                    ].join(" ")}
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={!isComplete || isLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-glow-brand-sm hover:shadow-glow-brand cursor-pointer"
              >
                {isLoading ? (
                  <>
                    <div className="relative w-4 h-4">
                      <div className="absolute inset-0 rounded-full border-2 border-white/20" />
                      <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white animate-spin" />
                    </div>
                    Verifying…
                  </>
                ) : (
                  <>
                    Verify and continue
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            <div className="mt-5 flex items-center justify-between text-[11px]">
              <button
                onClick={onBack}
                className="text-slate-600 hover:text-slate-400 transition-colors flex items-center gap-1 cursor-pointer"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
                Back to sign in
              </button>
              <button
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className="text-brand-400 hover:text-brand-300 disabled:text-slate-600 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
